/**
 * Récurrence — sous-ensemble de RRULE (RFC 5545)
 *
 * Le format de stockage est une RRULE, pas un format maison : c'est ce qui rend
 * l'export iCal possible sans traduction, et ce qui évite de réinventer une
 * sémantique de calendrier (mois courts, années bissextiles, rangs de semaine)
 * qui existe déjà et qui est éprouvée.
 *
 * Ce qui est supporté, et rien de plus :
 *
 *   FREQ        DAILY | WEEKLY | MONTHLY | YEARLY
 *   INTERVAL    entier ≥ 1
 *   BYDAY       MO..SU, avec rang optionnel en MONTHLY/YEARLY (1SA, -1SA)
 *   BYMONTHDAY  1..31 et -1..-31 (négatif = depuis la fin du mois)
 *   BYMONTH     1..12
 *   COUNT       nombre total d'occurrences depuis le départ
 *   UNTIL       date de fin incluse
 *   WKST        toléré uniquement à MO (semaine française)
 *
 * Tout le reste (BYSETPOS, BYWEEKNO, BYYEARDAY, FREQ horaire…) est rejeté à
 * l'analyse plutôt qu'ignoré silencieusement : une règle qu'on ne sait pas
 * calculer ne doit pas entrer en base.
 *
 * Deux points de sémantique RFC qui comptent pour le produit :
 *
 *  1. Les occurrences sont engendrées *depuis le départ* (DTSTART). Changer le
 *     départ change toute la série — c'est exactement ce qu'on veut quand une
 *     tâche se recale sur sa date de réalisation.
 *  2. Une occurrence impossible est **sautée**, pas tronquée : `BYMONTHDAY=31`
 *     ne produit rien en février, `FREQ=YEARLY` sur un 29 février ne produit
 *     rien les années non bissextiles. Pour « le dernier jour du mois », la
 *     règle correcte est `BYMONTHDAY=-1`, et `decrisRrule()` le dit à
 *     l'utilisateur au lieu de le laisser découvrir le trou.
 */
import { ajouteJours, dateIsoOuNull, enClair, estDateIso, jourSemaine, joursDansMois, lundiDeLaSemaine, nomMois, parties, } from './dates.js';
export class ErreurRrule extends Error {
}
export const CODES_JOUR = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const FREQUENCES = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
/** 1 = lundi … 7 = dimanche, aligné sur `jourSemaine()`. */
function numeroDeCode(code) {
    return CODES_JOUR.indexOf(code) + 1;
}
const RE_BYDAY = /^([+-]?\d{1,2})?(MO|TU|WE|TH|FR|SA|SU)$/;
function entier(cle, valeur) {
    if (!/^[+-]?\d+$/.test(valeur))
        throw new ErreurRrule(`${cle} : entier attendu, reçu « ${valeur} »`);
    return Number(valeur);
}
/** Analyse une RRULE textuelle. Lève `ErreurRrule` sur tout ce qui sort du sous-ensemble. */
export function analyseRrule(texte) {
    if (typeof texte !== 'string' || !texte.trim())
        throw new ErreurRrule('règle vide');
    const brut = texte.trim().replace(/^RRULE:/i, '');
    const r = {
        freq: 'DAILY',
        interval: 1,
        parJour: [],
        parJourDuMois: [],
        parMois: [],
        compte: null,
        jusqua: null,
    };
    let freqVue = false;
    for (const morceau of brut.split(';')) {
        if (!morceau.trim())
            continue;
        const i = morceau.indexOf('=');
        if (i < 0)
            throw new ErreurRrule(`fragment illisible « ${morceau} »`);
        const cle = morceau.slice(0, i).trim().toUpperCase();
        const valeur = morceau.slice(i + 1).trim();
        switch (cle) {
            case 'FREQ': {
                const f = valeur.toUpperCase();
                if (!FREQUENCES.includes(f)) {
                    throw new ErreurRrule(`FREQ « ${valeur} » hors du sous-ensemble (DAILY, WEEKLY, MONTHLY, YEARLY)`);
                }
                r.freq = f;
                freqVue = true;
                break;
            }
            case 'INTERVAL': {
                const n = entier(cle, valeur);
                if (n < 1)
                    throw new ErreurRrule(`INTERVAL doit être ≥ 1, reçu ${n}`);
                r.interval = n;
                break;
            }
            case 'BYDAY': {
                for (const brutJour of valeur.split(',')) {
                    const m = RE_BYDAY.exec(brutJour.trim().toUpperCase());
                    if (!m)
                        throw new ErreurRrule(`BYDAY : « ${brutJour} » illisible`);
                    const rang = m[1] === undefined ? null : Number(m[1]);
                    if (rang !== null && (rang === 0 || Math.abs(rang) > 5)) {
                        throw new ErreurRrule(`BYDAY : rang ${rang} hors bornes (±1 à ±5)`);
                    }
                    r.parJour.push({ rang, jour: m[2] });
                }
                break;
            }
            case 'BYMONTHDAY': {
                for (const brutJm of valeur.split(',')) {
                    const n = entier(cle, brutJm.trim());
                    if (n === 0 || Math.abs(n) > 31)
                        throw new ErreurRrule(`BYMONTHDAY hors bornes : ${n}`);
                    r.parJourDuMois.push(n);
                }
                break;
            }
            case 'BYMONTH': {
                for (const brutMois of valeur.split(',')) {
                    const n = entier(cle, brutMois.trim());
                    if (n < 1 || n > 12)
                        throw new ErreurRrule(`BYMONTH hors bornes : ${n}`);
                    r.parMois.push(n);
                }
                break;
            }
            case 'COUNT': {
                const n = entier(cle, valeur);
                if (n < 1)
                    throw new ErreurRrule(`COUNT doit être ≥ 1, reçu ${n}`);
                r.compte = n;
                break;
            }
            case 'UNTIL': {
                const m = /^(\d{4})(\d{2})(\d{2})(T\d{6}Z?)?$/.exec(valeur.toUpperCase());
                if (!m)
                    throw new ErreurRrule(`UNTIL : format AAAAMMJJ attendu, reçu « ${valeur} »`);
                const d = `${m[1]}-${m[2]}-${m[3]}`;
                if (!estDateIso(d))
                    throw new ErreurRrule(`UNTIL : date inexistante « ${valeur} »`);
                r.jusqua = d;
                break;
            }
            case 'WKST': {
                if (valeur.toUpperCase() !== 'MO') {
                    throw new ErreurRrule('WKST : seule la semaine commençant le lundi est supportée');
                }
                break;
            }
            default:
                throw new ErreurRrule(`« ${cle} » hors du sous-ensemble supporté`);
        }
    }
    if (!freqVue)
        throw new ErreurRrule('FREQ est obligatoire');
    if (r.compte !== null && r.jusqua !== null)
        throw new ErreurRrule('COUNT et UNTIL sont exclusifs (RFC 5545)');
    if (r.parJour.some((j) => j.rang !== null) && r.freq !== 'MONTHLY' && r.freq !== 'YEARLY') {
        throw new ErreurRrule('un rang dans BYDAY (ex. 1SA) n\'a de sens qu\'en FREQ=MONTHLY ou YEARLY');
    }
    if (r.parJourDuMois.length > 0 && r.freq === 'WEEKLY') {
        throw new ErreurRrule('BYMONTHDAY est interdit en FREQ=WEEKLY (RFC 5545)');
    }
    return r;
}
/** Sérialise dans l'ordre canonique, pour comparer et stocker de façon stable. */
export function serialiseRrule(r) {
    const bouts = [`FREQ=${r.freq}`];
    if (r.interval !== 1)
        bouts.push(`INTERVAL=${r.interval}`);
    if (r.parMois.length)
        bouts.push(`BYMONTH=${[...r.parMois].sort((a, b) => a - b).join(',')}`);
    if (r.parJourDuMois.length)
        bouts.push(`BYMONTHDAY=${[...r.parJourDuMois].sort((a, b) => a - b).join(',')}`);
    if (r.parJour.length) {
        bouts.push(`BYDAY=${r.parJour
            .slice()
            .sort((a, b) => (a.rang ?? 0) - (b.rang ?? 0) || numeroDeCode(a.jour) - numeroDeCode(b.jour))
            .map((j) => `${j.rang ?? ''}${j.jour}`)
            .join(',')}`);
    }
    if (r.compte !== null)
        bouts.push(`COUNT=${r.compte}`);
    if (r.jusqua !== null)
        bouts.push(`UNTIL=${r.jusqua.replace(/-/g, '')}`);
    return bouts.join(';');
}
export function normaliseRrule(texte) {
    return serialiseRrule(analyseRrule(texte));
}
// ── Génération des occurrences ──────────────────────────────────────────────
/** Bornes de sûreté : une règle ne doit jamais faire tourner la boucle indéfiniment. */
const MAX_PERIODES = 40_000;
/** `FREQ=DAILY;BYMONTH=9` a ~335 périodes stériles d'affilée : la borne doit les absorber. */
const MAX_PERIODES_STERILES = 1_200;
/** Jours candidats d'un mois donné, selon BYMONTHDAY / BYDAY, sinon le jour du départ. */
function joursDuMois(annee, mois, r, jourDuDepart) {
    const nbJours = joursDansMois(annee, mois);
    const out = [];
    if (r.parJourDuMois.length > 0) {
        for (const jm of r.parJourDuMois) {
            const jour = jm > 0 ? jm : nbJours + 1 + jm; // -1 → dernier jour
            const d = dateIsoOuNull(annee, mois, jour);
            if (d)
                out.push(d);
        }
        // RFC : en présence des deux, BYDAY *filtre* BYMONTHDAY (le rang est alors ignoré).
        if (r.parJour.length > 0) {
            const voulus = new Set(r.parJour.map((j) => numeroDeCode(j.jour)));
            return out.filter((d) => voulus.has(jourSemaine(d)));
        }
        return out;
    }
    if (r.parJour.length > 0) {
        for (const regle of r.parJour) {
            const cible = numeroDeCode(regle.jour);
            if (regle.rang === null) {
                for (let jour = 1; jour <= nbJours; jour++) {
                    const d = dateIsoOuNull(annee, mois, jour);
                    if (d && jourSemaine(d) === cible)
                        out.push(d);
                }
            }
            else if (regle.rang > 0) {
                const premier = dateIsoOuNull(annee, mois, 1);
                const decalage = (cible - jourSemaine(premier) + 7) % 7;
                const jour = 1 + decalage + (regle.rang - 1) * 7;
                const d = dateIsoOuNull(annee, mois, jour);
                if (d)
                    out.push(d);
            }
            else {
                const dernier = dateIsoOuNull(annee, mois, nbJours);
                const decalage = (jourSemaine(dernier) - cible + 7) % 7;
                const jour = nbJours - decalage + (regle.rang + 1) * 7;
                const d = dateIsoOuNull(annee, mois, jour);
                if (d)
                    out.push(d);
            }
        }
        return out;
    }
    const d = dateIsoOuNull(annee, mois, jourDuDepart);
    return d ? [d] : [];
}
/** Candidats de la k-ième période de la règle (k = 0 → période du départ). */
function candidatsPeriode(depart, r, k) {
    const p = parties(depart);
    switch (r.freq) {
        case 'DAILY':
            return [ajouteJours(depart, k * r.interval)];
        case 'WEEKLY': {
            const debutSemaine = ajouteJours(lundiDeLaSemaine(depart), k * r.interval * 7);
            if (r.parJour.length === 0)
                return [ajouteJours(debutSemaine, jourSemaine(depart) - 1)];
            return r.parJour.map((j) => ajouteJours(debutSemaine, numeroDeCode(j.jour) - 1));
        }
        case 'MONTHLY': {
            const total = p.annee * 12 + (p.mois - 1) + k * r.interval;
            return joursDuMois(Math.floor(total / 12), (total % 12) + 1, r, p.jour);
        }
        case 'YEARLY': {
            const annee = p.annee + k * r.interval;
            const moisCibles = r.parMois.length ? [...r.parMois].sort((a, b) => a - b) : [p.mois];
            return moisCibles.flatMap((mois) => joursDuMois(annee, mois, r, p.jour));
        }
    }
}
/** Filtres appliqués *après* expansion (BYMONTH limite, BYDAY limite en DAILY). */
function passeLesFiltres(d, r) {
    if (r.parMois.length > 0 && r.freq !== 'YEARLY' && !r.parMois.includes(parties(d).mois))
        return false;
    if (r.freq === 'DAILY' && r.parJour.length > 0) {
        const voulus = new Set(r.parJour.map((j) => numeroDeCode(j.jour)));
        if (!voulus.has(jourSemaine(d)))
            return false;
    }
    return true;
}
/**
 * Engendre les occurrences de la série, dans l'ordre croissant.
 * `depart` joue le rôle de DTSTART : il fait partie de la série s'il satisfait la règle.
 */
export function* genereOccurrences(depart, regle, options = {}) {
    const r = typeof regle === 'string' ? analyseRrule(regle) : regle;
    const seuil = options.apres !== undefined ? ajouteJours(options.apres, 1) : (options.depuis ?? depart);
    let emises = 0;
    let steriles = 0;
    for (let k = 0; k < MAX_PERIODES; k++) {
        const candidats = [...new Set(candidatsPeriode(depart, r, k))]
            .filter((d) => passeLesFiltres(d, r))
            .sort();
        let productive = false;
        for (const d of candidats) {
            if (d < depart)
                continue; // rien avant DTSTART
            productive = true;
            if (r.jusqua !== null && d > r.jusqua)
                return;
            emises++;
            if (r.compte !== null && emises > r.compte)
                return;
            if (d >= seuil)
                yield d;
        }
        steriles = productive ? 0 : steriles + 1;
        if (steriles > MAX_PERIODES_STERILES)
            return;
    }
}
/** Les `nombre` premières occurrences (utile pour l'aperçu du sélecteur de récurrence). */
export function occurrences(depart, regle, nombre = 3, options = {}) {
    const out = [];
    for (const d of genereOccurrences(depart, regle, options)) {
        out.push(d);
        if (out.length >= nombre)
            break;
    }
    return out;
}
/** Première occurrence strictement postérieure à `apres`. */
export function prochaineOccurrence(depart, regle, apres) {
    return occurrences(depart, regle, 1, { apres })[0] ?? null;
}
/**
 * Première occurrence à partir de `depuis` (incluse).
 * C'est la garde qui évite de programmer des rappels dans le passé quand on
 * déclare un équipement ancien : on avance jusqu'à la prochaine échéance réelle.
 */
export function premiereOccurrenceDepuis(depart, regle, depuis) {
    return occurrences(depart, regle, 1, { depuis })[0] ?? null;
}
// ── Description en français ─────────────────────────────────────────────────
const NOM_JOUR = {
    MO: 'lundi',
    TU: 'mardi',
    WE: 'mercredi',
    TH: 'jeudi',
    FR: 'vendredi',
    SA: 'samedi',
    SU: 'dimanche',
};
const RANGS = {
    1: 'premier',
    2: 'deuxième',
    3: 'troisième',
    4: 'quatrième',
    5: 'cinquième',
};
function listeFr(elements) {
    if (elements.length <= 1)
        return elements[0] ?? '';
    return `${elements.slice(0, -1).join(', ')} et ${elements[elements.length - 1]}`;
}
function ordinalJour(n) {
    return n === 1 ? '1er' : String(n);
}
function decrisJoursDuMois(valeurs) {
    return listeFr([...valeurs]
        .sort((a, b) => a - b)
        .map((n) => {
        if (n === -1)
            return 'le dernier jour du mois';
        if (n < 0)
            return `le ${-n}ᵉ jour avant la fin du mois`;
        return `le ${ordinalJour(n)}`;
    }));
}
function decrisJoursSemaine(jours) {
    return listeFr(jours.map((j) => {
        if (j.rang === null)
            return `le ${NOM_JOUR[j.jour]}`;
        if (j.rang === -1)
            return `le dernier ${NOM_JOUR[j.jour]}`;
        if (j.rang < 0)
            return `le ${RANGS[-j.rang] ?? `${-j.rang}ᵉ`} ${NOM_JOUR[j.jour]} avant la fin du mois`;
        return `le ${RANGS[j.rang] ?? `${j.rang}ᵉ`} ${NOM_JOUR[j.jour]}`;
    }));
}
/**
 * La règle écrite en français, telle qu'affichée en direct dans le sélecteur.
 * Signale explicitement les mois sautés : c'est la seule façon honnête de
 * présenter `BYMONTHDAY=31`, dont l'utilisateur ne devinerait pas le trou.
 */
export function decrisRrule(regle) {
    const r = typeof regle === 'string' ? analyseRrule(regle) : regle;
    const n = r.interval;
    let s;
    switch (r.freq) {
        case 'DAILY':
            s = n === 1 ? 'tous les jours' : `tous les ${n} jours`;
            break;
        case 'WEEKLY':
            s = n === 1 ? 'toutes les semaines' : `toutes les ${n} semaines`;
            break;
        case 'MONTHLY':
            s = n === 1 ? 'tous les mois' : `tous les ${n} mois`;
            break;
        case 'YEARLY':
            s = n === 1 ? 'tous les ans' : `tous les ${n} ans`;
            break;
    }
    if (r.parMois.length > 0) {
        s += `, en ${listeFr([...r.parMois].sort((a, b) => a - b).map(nomMois))}`;
    }
    if (r.parJourDuMois.length > 0) {
        s += `, ${decrisJoursDuMois(r.parJourDuMois)}`;
        if (r.parJour.length > 0)
            s += ` si c'est ${decrisJoursSemaine(r.parJour).replace(/^le /, 'un ')}`;
    }
    else if (r.parJour.length > 0) {
        s += ` ${decrisJoursSemaine(r.parJour)}`;
    }
    if (r.parJourDuMois.some((jm) => jm >= 29)) {
        s += ' (les mois plus courts sont sautés)';
    }
    if (r.compte !== null)
        s += `, ${r.compte} fois`;
    if (r.jusqua !== null)
        s += `, jusqu'au ${enClair(r.jusqua)}`;
    return s;
}
/** Forme courte pour le badge de la carte de tâche : « tous les 2 ans », « 3 mois ». */
export function resumeRrule(regle) {
    const r = typeof regle === 'string' ? analyseRrule(regle) : regle;
    const n = r.interval;
    switch (r.freq) {
        case 'DAILY':
            return n === 1 ? 'chaque jour' : `tous les ${n} j`;
        case 'WEEKLY':
            return n === 1 ? 'chaque semaine' : `toutes les ${n} sem.`;
        case 'MONTHLY':
            return n === 1 ? 'tous les mois' : `tous les ${n} mois`;
        case 'YEARLY':
            return n === 1 ? 'tous les ans' : `tous les ${n} ans`;
    }
}
//# sourceMappingURL=rrule.js.map